import type { JsonObject, ToolDef } from '@fabric-harness/sdk';
import { parseStatementRows, runStatement, type DatabricksStatementClient } from './sql-exec.js';

export type ConsumptionGroupBy = 'sku' | 'day';

// Allowlisted group expressions — never interpolated from user/model input.
const GROUP_COLUMNS: Record<ConsumptionGroupBy, { select: string; group: string }> = {
  sku: { select: 'u.sku_name AS sku, u.usage_unit AS unit', group: 'u.sku_name, u.usage_unit' },
  day: { select: 'u.usage_date AS day', group: 'u.usage_date' },
};

export interface ConsumptionSummaryOptions {
  /** Inclusive start date, `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive end date, `YYYY-MM-DD`. Defaults to `startDate`'s caller-provided window end. */
  endDate: string;
  groupBy?: ConsumptionGroupBy;
}

export interface ConsumptionSummary {
  startDate: string;
  endDate: string;
  groupBy: ConsumptionGroupBy;
  rows: JsonObject[];
  totalDbus: number;
  totalListCostUsd: number;
}

export interface DatabricksConsumptionOptions {
  warehouseId: string;
  pollIntervalMs?: number;
  timeoutMs?: number;
}

function buildQuery(groupBy: ConsumptionGroupBy): string {
  const g = GROUP_COLUMNS[groupBy];
  return [
    `SELECT ${g.select},`,
    '       SUM(u.usage_quantity) AS quantity,',
    '       SUM(u.usage_quantity * lp.pricing.default) AS list_cost_usd',
    'FROM system.billing.usage u',
    'LEFT JOIN system.billing.list_prices lp',
    '  ON u.cloud = lp.cloud AND u.sku_name = lp.sku_name AND u.usage_unit = lp.usage_unit',
    ' AND u.usage_end_time >= lp.price_start_time',
    ' AND (lp.price_end_time IS NULL OR u.usage_end_time < lp.price_end_time)',
    'WHERE u.usage_date >= :start_date AND u.usage_date <= :end_date',
    `GROUP BY ${g.group}`,
    'ORDER BY list_cost_usd DESC',
  ].join('\n');
}

function toNumber(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * System-Tables consumption reporting. Reads `system.billing.usage` joined to `list_prices` to report
 * real DBUs + list cost — the "prove the consumption" artifact for chargeback and partner credit.
 */
export function databricksConsumption(client: DatabricksStatementClient, options: DatabricksConsumptionOptions) {
  const poll = { intervalMs: options.pollIntervalMs ?? 2000, timeoutMs: options.timeoutMs ?? 120_000 };
  return {
    async summary(opts: ConsumptionSummaryOptions): Promise<ConsumptionSummary> {
      const groupBy = opts.groupBy ?? 'sku';
      const response = await runStatement(
        client,
        options.warehouseId,
        buildQuery(groupBy),
        [
          { name: 'start_date', value: opts.startDate, type: 'DATE' },
          { name: 'end_date', value: opts.endDate, type: 'DATE' },
        ],
        poll,
      );
      const rows = parseStatementRows(response);
      return {
        startDate: opts.startDate,
        endDate: opts.endDate,
        groupBy,
        rows,
        totalDbus: rows.reduce((sum, r) => sum + toNumber(r.quantity), 0),
        totalListCostUsd: rows.reduce((sum, r) => sum + toNumber(r.list_cost_usd), 0),
      };
    },
  };
}

export type DatabricksConsumption = ReturnType<typeof databricksConsumption>;

export interface DatabricksConsumptionToolOptions extends DatabricksConsumptionOptions {
  name?: string;
  description?: string;
  /** Clock injection for tests / determinism. */
  now?: () => Date;
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Agent-callable consumption report over the last `days` (default 30) or an explicit date window. */
export function databricksConsumptionTool(client: DatabricksStatementClient, options: DatabricksConsumptionToolOptions): ToolDef<{ days?: number; startDate?: string; endDate?: string; groupBy?: ConsumptionGroupBy }, unknown> {
  const reporter = databricksConsumption(client, options);
  const now = options.now ?? (() => new Date());
  return {
    name: options.name ?? 'databricks_consumption',
    description: options.description ?? 'Report real Databricks consumption (DBUs and list cost) from system.billing over a date window.',
    inputSchema: {
      type: 'object',
      additionalProperties: false,
      properties: {
        days: { type: 'number', minimum: 1 },
        startDate: { type: 'string' },
        endDate: { type: 'string' },
        groupBy: { type: 'string', enum: ['sku', 'day'] },
      },
    },
    metadata: { effect: 'read', provider: 'databricks', service: 'system-tables' },
    execute: (input) => {
      const endDate = input.endDate ?? isoDate(now());
      let startDate = input.startDate;
      if (!startDate) {
        const start = now();
        start.setUTCDate(start.getUTCDate() - (input.days ?? 30));
        startDate = isoDate(start);
      }
      return reporter.summary({ startDate, endDate, ...(input.groupBy ? { groupBy: input.groupBy } : {}) });
    },
  };
}
